/**
 * Contextual Quick Actions for the Sales Chatbot
 *
 * Returns step-specific quick action buttons when the user is on a
 * quick-flow form, or default actions otherwise.
 */

import type { QuickFlowContext, QuickAction } from "@/lib/chat/types";

// ---------------------------------------------------------------------------
// Default actions (shown on non-form pages)
// ---------------------------------------------------------------------------

const DEFAULT_ACTIONS: QuickAction[] = [
  {
    key: "sell",
    label: "Kompensation verkaufen",
    // The message names the assets a landowner recognises, and keeps the verb
    // ("verkaufen") that the prompt's VERKÄUFER-Signale list reads — the label
    // is the umbrella, the utterance stays the way a landowner would phrase it.
    // `intent-signals.test.ts` pins that coupling. It states the role and asks nothing: a
    // trailing „Wie funktioniert das?“ got a long process overview with the capture question
    // only at its end (`quick-actions.test.ts`, `seller-quick-action.eval.test.ts`).
    message: "Ich möchte Kompensation verkaufen — meine Flächen oder meine Ökopunkte.",
  },
  {
    key: "buy",
    label: "Kompensation beschaffen",
    message: "Ich suche Kompensation für mein Vorhaben. Können Sie mir helfen?",
  },
  {
    key: "learn",
    label: "Was sind Ökopunkte?",
    message: "Was sind Ökopunkte und wie funktioniert der Handel?",
  },
];

// ---------------------------------------------------------------------------
// Schnellanfrage (Buyer) — V2 step-specific actions
// ---------------------------------------------------------------------------

const ANFRAGE_ACTIONS: Record<string, QuickAction[]> = {
  location: [
    {
      key: "why_location",
      label: "Warum ist der Standort wichtig?",
      message: "Warum muss ich eine Postleitzahl angeben? Wofür ist der Standort wichtig?",
    },
    {
      key: "what_naturraum",
      label: "Was ist ein Naturraum?",
      message: "Was ist ein Naturraum und warum ist er für die Kompensation wichtig?",
    },
    {
      key: "which_plz",
      label: "Welche PLZ soll ich angeben?",
      message:
        "Welche Postleitzahl soll ich hier eingeben — die meines Büros oder die des Projektstandorts?",
    },
  ],
  quantity: [
    {
      key: "how_find",
      label: "Wie finde ich die Menge?",
      message: "Wie finde ich heraus, wie viele Ökopunkte ich brauche?",
    },
    {
      key: "typical_amounts",
      label: "Typische Mengen?",
      message: "Wie viele Ökopunkte braucht man typischerweise für ein Projekt?",
    },
    {
      key: "current_prices",
      label: "Was kosten Ökopunkte aktuell?",
      message: "Was kosten Ökopunkte aktuell in meiner Region?",
    },
  ],
  contact: [
    {
      key: "what_happens",
      label: "Was passiert nach Absenden?",
      message: "Was passiert, nachdem ich meine Anfrage abgeschickt habe?",
    },
    {
      key: "how_matching",
      label: "Wie funktioniert das Matching?",
      message: "Wie findet die Plattform passende Angebote für meine Anfrage?",
    },
    {
      key: "can_change",
      label: "Kann ich nachträglich ändern?",
      message: "Kann ich meine Anfrage nach dem Absenden noch ändern?",
    },
  ],
};

// ---------------------------------------------------------------------------
// Schnellangebot (Seller) — V2 step-specific actions
// ---------------------------------------------------------------------------

const ANGEBOT_ACTIONS: Record<string, QuickAction[]> = {
  location: [
    {
      key: "which_plz",
      label: "Welche PLZ für mein Ökokonto?",
      message: "Welche Postleitzahl soll ich für mein Ökokonto angeben?",
    },
    {
      key: "what_naturraum",
      label: "Was ist ein Naturraum?",
      message: "Was ist ein Naturraum und warum ist er für den Verkauf wichtig?",
    },
    {
      key: "multiple_locations",
      label: "Mehrere Standorte?",
      message: "Was wenn mein Ökokonto mehrere Standorte hat?",
    },
  ],
  offer: [
    {
      key: "how_many",
      label: "Wie viele Punkte verkaufen?",
      message: "Wie viele Ökopunkte kann ich aus meinem Ökokonto verkaufen?",
    },
    {
      key: "good_price",
      label: "Was ist ein guter Preis?",
      message: "Was ist ein guter Verkaufspreis für meine Ökopunkte?",
    },
    {
      key: "market_prices",
      label: "Aktuelle Marktpreise?",
      message: "Wie sehen die aktuellen Marktpreise für Ökopunkte in meiner Region aus?",
    },
  ],
  contact: [
    {
      key: "what_happens",
      label: "Was passiert nach Absenden?",
      message: "Was passiert, nachdem ich mein Angebot abgeschickt habe?",
    },
    {
      key: "how_matching",
      label: "Wie funktioniert das Matching?",
      message: "Wie findet die Plattform passende Käufer für mein Angebot?",
    },
    {
      key: "can_change",
      label: "Nachträglich ändern?",
      message: "Kann ich mein Angebot nach dem Absenden noch ändern?",
    },
  ],
};

// ---------------------------------------------------------------------------
// "Create order for me" nudge — shown on contact step
// ---------------------------------------------------------------------------

const ANFRAGE_CREATE_ACTION: QuickAction = {
  key: "create_for_me",
  label: "Anfrage für mich erstellen",
  message: "Können Sie meine Kaufanfrage mit den bisherigen Angaben für mich erstellen?",
};

const ANGEBOT_CREATE_ACTION: QuickAction = {
  key: "create_for_me",
  label: "Angebot für mich erstellen",
  message: "Können Sie mein Verkaufsangebot mit den bisherigen Angaben für mich erstellen?",
};

/** Step index threshold: show "create for me" from this step onwards (0-based) */
const CREATE_FROM_STEP_INDEX = 2;

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Get quick actions for the current context.
 * Returns step-specific actions on form pages, defaults otherwise.
 * Appends a "create order for me" action on the contact step.
 */
export function getQuickActionsForContext(ctx: QuickFlowContext | null): QuickAction[] {
  if (!ctx) return DEFAULT_ACTIONS;

  const stepActions =
    ctx.flow === "schnellanfrage" ? ANFRAGE_ACTIONS[ctx.step] : ANGEBOT_ACTIONS[ctx.step];

  const actions = stepActions ?? DEFAULT_ACTIONS;

  // Add "create for me" nudge on the contact step
  if (ctx.stepIndex >= CREATE_FROM_STEP_INDEX) {
    const createAction =
      ctx.flow === "schnellanfrage" ? ANFRAGE_CREATE_ACTION : ANGEBOT_CREATE_ACTION;
    return [...actions, createAction];
  }

  return actions;
}
